import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { SupplyConnection, SupplySourceState, SupplySubject } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Shared pieces of «Software prodotto»: the labels of the sources, the
 * severity and pipeline badges, the subjects (project or artifact), the
 * state of a connection's background pass, the row of source tabs.
 */

export function etichettaFonte(f: string): string {
  return ({
    gitlab_pipeline: t('Pipeline GitLab'),
    artifactory_attached: t('Artifactory'),
    nexus_attached: t('Nexus'),
    gitlab_dependency_list: t('GitLab Ultimate'),
    censo: t('CENSO'),
    xray: t('Xray'),
    nexus_iq: t('Nexus IQ'),
    gitlab: t('GitLab'),
    artifactory: t('Artifactory'),
    nexus: t('Nexus'),
  } as Record<string, string>)[f] ?? f
}

export function etichettaRegistro(p: string): string {
  return ({ artifactory: t('Artifactory'), nexus: t('Nexus'), gitlab: t('GitLab Registry') } as Record<string, string>)[p] ?? p
}

export function motivoFonte(m: string): string {
  return ({
    'nessuna connessione GitLab': t('nessuna connessione GitLab'),
    'nessuna pipeline ha ancora prodotto un SBOM': t('nessuna pipeline ha ancora prodotto un SBOM'),
    'nessuna connessione Artifactory': t('nessuna connessione Artifactory'),
    'nessun SBOM pubblicato accanto agli artefatti': t('nessun SBOM pubblicato accanto agli artefatti'),
    'nessuna connessione Nexus': t('nessuna connessione Nexus'),
    'nessun SBOM pubblicato accanto ai componenti': t('nessun SBOM pubblicato accanto ai componenti'),
    'GitLab Ultimate non rilevato': t('GitLab Ultimate non rilevato'),
    'nessun SBOM da valutare': t('nessun SBOM da valutare'),
    'Xray non rilevato su Artifactory': t('Xray non rilevato su Artifactory'),
    'nessun indirizzo di Nexus IQ nella connessione Nexus': t('nessun indirizzo di Nexus IQ nella connessione Nexus'),
  } as Record<string, string>)[m] ?? m
}

const GRAVITA: Record<string, [string, () => string]> = {
  critical: ['badge--error', () => t('critica')],
  high: ['badge--warn', () => t('alta')],
  medium: ['badge--info', () => t('media')],
  low: ['badge--muted', () => t('bassa')],
}

/** A severity as a badge; an external source's own word is kept when unknown. */
export function BadgeGravita({ g }: { g: string }) {
  const v = GRAVITA[(g || '').toLowerCase()]
  if (!g) return <span className="text-muted">—</span>
  return <span className={`badge ${v ? v[0] : 'badge--muted'}`}>{v ? v[1]() : g}</span>
}

const STATI_PIPELINE: Record<string, [string, () => string]> = {
  success: ['badge--ok', () => t('riuscita')],
  failed: ['badge--error', () => t('fallita')],
  running: ['badge--info', () => t('in corso')],
  pending: ['badge--info', () => t('in attesa')],
  created: ['badge--info', () => t('creata')],
  canceled: ['badge--muted', () => t('annullata')],
  skipped: ['badge--muted', () => t('saltata')],
  manual: ['badge--muted', () => t('manuale')],
}

export function BadgePipeline({ stato }: { stato?: string }) {
  if (!stato) return <span className="text-muted">—</span>
  const v = STATI_PIPELINE[stato]
  return <span className={`badge ${v ? v[0] : 'badge--muted'}`}>{v ? v[1]() : stato}</span>
}

/** An external link, only when it is http(s) (the server already filters). */
export function LinkEsterno({ href, children }: { href: string; children: ReactNode }) {
  if (!/^https?:\/\//i.test(href || '')) return <>{children}</>
  return <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>
}

export function Soggetto({ s }: { s: SupplySubject }) {
  if (s.kind === 'project' && s.id) {
    return (
      <>
        <Link to={`/software-supply/projects/${s.id}`}>{s.label || '—'}</Link>
        {s.ref && <small className="text-muted"> · <span className="mono">{s.ref}</span></small>}
        {s.is_tag && <> <span className="badge badge--info">{t('rilascio')}</span></>}
      </>
    )
  }
  return <>{s.label || '—'}{s.kind === 'artifact' && <small className="text-muted"> · {t('artefatto')}</small>}</>
}

export const quando = (iso: string | null | undefined) =>
  (iso ? new Date(iso).toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'short' }) : '—')

export function etichettaConnessione(k: string): string {
  return ({ gitlab: t('GitLab'), artifactory: t('JFrog Artifactory'), nexus: t('Sonatype Nexus Repository') } as Record<string, string>)[k] ?? k
}

/** The state of one connection's pass, in words. */
export function StatoConnessione({ c }: { c: SupplyConnection }) {
  const run = c.last_run
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      <strong>{c.name}</strong>
      <small className="text-muted">{etichettaConnessione(c.kind)}{c.version ? ` ${c.version}` : ''}</small>
      {c.running ? <span className="badge badge--info">{t('giro in corso')}</span>
        : c.requested ? <span className="badge badge--info">{t('giro richiesto')}</span> : null}
      {c.last_error && <span className="badge badge--error" title={c.last_error}>{t('ultimo giro non riuscito')}</span>}
      {c.xray && <span className="badge badge--ok">{t('Xray {v}', { v: c.xray })}</span>}
      {c.nexus_iq && <span className="badge badge--ok">{t('Nexus IQ')}</span>}
      {c.ultimate && <span className="badge badge--ok">{t('GitLab Ultimate')}</span>}
      <small className="text-muted" style={{ flexBasis: '100%' }}>
        {c.last_finished_at
          ? t('Ultimo giro {quando}: {n} richieste{r}', {
            quando: quando(c.last_finished_at), n: run?.requests ?? 0,
            r: run?.throttled ? t(', rallentato {m} volte dal sistema', { m: run.throttled }) : '',
          })
          : t('Nessun giro ancora')}
        {c.next_due_at && ` · ${t('prossimo {quando}', { quando: quando(c.next_due_at) })}`}
        {c.requests_today != null && ` · ${t('{n} richieste oggi', { n: c.requests_today })}`}
      </small>
      {c.last_error && <small style={{ color: 'var(--error)', flexBasis: '100%' }}>{c.last_error}</small>}
      {run?.note && run.outcome !== 'failed' && <small className="text-muted" style={{ flexBasis: '100%' }}>{run.note}</small>}
    </div>
  )
}

/** The row of source tabs: one per source, with its count, plus the comparison. */
export function TabFonti({ fonti, attiva, onScegli, conteggi, confronto = true }: {
  fonti: SupplySourceState[]
  attiva: string
  onScegli: (f: string) => void
  conteggi?: Record<string, number | undefined>
  confronto?: boolean
}) {
  return (
    <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }} role="tablist">
      {fonti.map((f) => (
        <button key={f.source} type="button" role="tab" aria-selected={attiva === f.source}
          className={attiva === f.source ? '' : 'secondary'} onClick={() => onScegli(f.source)}
          title={f.available ? undefined : motivoFonte(f.reason)}>
          {etichettaFonte(f.source)}
          {conteggi?.[f.source] != null ? ` (${conteggi[f.source]})` : ''}
          {!f.available && <small className="text-muted"> · {t('non disponibile')}</small>}
        </button>
      ))}
      {confronto && (
        <button type="button" role="tab" aria-selected={attiva === 'confronto'}
          className={attiva === 'confronto' ? '' : 'secondary'} onClick={() => onScegli('confronto')}>
          {t('Confronto')}
        </button>
      )}
    </div>
  )
}
